import { useState, useEffect, forwardRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch, IconButton, PersonAvatar, IconTile } from '@teo/components';
import {
  CrossFilled,
  DuoToneTrajectoryFilled,
  ChevronDown,
  Training,
  Theme,
  Evaluation,
  Flag,
  DuoToneCourseFilled,
  DuoToneAssessmentFilled,
  DuoToneVacancyFilled,
} from '@teo/components/icons';
import { useGet } from '@/query/fetchHooks';
import { axios } from '@/axios';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { Userwithorganisationwithidps } from '@/models';
import { UserInvite } from '@/components/UserInvite/UserInvite';
import { config } from '@/config';
import { sortDataLP } from '@/components/LearningPath/util';
import { newUid } from '@/util';

const SpecifyItemContent = ({
  item,
  authUser,
  updateSpecifyItem,
  nodesTeachers,
  setContentTeachers,
  create,
  specifyItem,
  contentTeachers,
}: any) => {
  const { name, type, subType, picture, objectId, id } = item;
  const { t } = useTranslation();
  const [contentTeacher, setContentTeacher] = useState<any>(
    contentTeachers?.find((el: any) => el?.objectId === objectId)?.teachers ||
      []
  );
  const [teachers, setTeachers] = useState<any>([]);
  const [value, setValue] = useState<any>([]);
  const [lpTreeConrent, setLpTreeConrent] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const isAlreadyExists = contentTeachers?.find(
    (el: any) => el?.objectId === objectId
  );

  useGet(`/teachers`, {
    enabled:
      type === 'course' && !create && !isAlreadyExists && !updateSpecifyItem,
    courseId: item?.objectId,
    onSuccess: (res: any) => {
      if (res?.data) {
        const courseTeachers = res?.data?.map((el: any) => ({
          firstName: el?.firstName,
          lastName: el?.lastName,
          username: el?.username,
          label: el?.username,
          id: el?.id,
          key: el?.id,
          userId: el?.userId,
          picture: el?.picture,
        }));
        setContentTeacher([...courseTeachers]);
      } else {
        setContentTeacher([...nodesTeachers]);
      }
    },
  });

  useGet(`/trainingteachers`, {
    enabled:
      type === 'training' && !create && !isAlreadyExists && !updateSpecifyItem,
    trainingId: item?.objectId,
    onSuccess: (res: any) => {
      if (res?.data) {
        const courseTeachers = res?.data?.map((el: any) => ({
          firstName: el?.firstName,
          lastName: el?.lastName,
          username: el?.username,
          label: el?.username,
          id: el?.id,
          key: el?.id,
          userId: el?.userId,
          picture: el?.picture,
        }));

        setContentTeacher([...courseTeachers]);
      } else {
        setContentTeacher([...nodesTeachers]);
      }
    },
  });

  useGet(`/learningpathnodedetails`, {
    enabled: type === 'learningpath',
    learningpathId: objectId,
    organisationId: authUser?.activeOrganisationId,
    onSuccess: async (res: any) => {
      const nodedetails = res?.data?.filter((el: any) => el?.type !== 'root');
      if (nodedetails?.length > 0) {
        const sortData = sortDataLP(nodedetails);
        setLpTreeConrent(sortData || []);
      }
    },
  });

  const handleAddTeacherToList = (newValue: any) => {
    const newNewTeachers = contentTeacher.slice();
    for (const teacher of newValue) {
      const found = contentTeacher.find(
        (oldTeacher: any) => teacher.key === oldTeacher.key
      );
      if (!found) {
        teacher.lprole = 20;
        newNewTeachers.push(teacher);
      }
    }
    setContentTeacher(newNewTeachers);
    setTeachers([]);
  };

  const handleRemoveTeacher = (id: any) => {
    const removeTeacher = contentTeacher.filter((item: any) => item.key !== id);
    setContentTeacher(removeTeacher);
  };

  useEffect(() => {
    if ((create || updateSpecifyItem) && nodesTeachers?.length > 0 && !isOpen) {
      setIsOpen(true);
      contentTeacher?.length === 0 && setContentTeacher([...nodesTeachers]);
    }
    if (
      (create || updateSpecifyItem) &&
      nodesTeachers?.length === 0 &&
      !isOpen
    ) {
      setIsOpen(true);
    }
  }, [nodesTeachers]);

  useEffect(() => {
    if (contentTeacher) {
      const newItem = {
        id: objectId,
        objectId: objectId,
        type: type,
        teachers: contentTeacher,
        baseNodeId: id,
      };
      setContentTeachers((prev: any) => [
        ...prev.filter((item: any) => item.id !== newItem.id),
        newItem,
      ]);
    }
  }, [contentTeacher, isOpen]);

  return (
    <div className="mt-6 flex flex-col rounded-lg border p-4">
      <div className="flex items-center">
        <div className="mr-3">
          <IconTile
            Icon={
              subType === 'training'
                ? Training
                : subType === 'learningpath'
                ? Theme
                : subType === 'milestone'
                ? Flag
                : subType === 'evaluation'
                ? Evaluation
                : subType === 'course'
                ? DuoToneCourseFilled
                : subType === 'assessment'
                ? DuoToneAssessmentFilled
                : subType === 'leervacature'
                ? DuoToneVacancyFilled
                : DuoToneCourseFilled
            }
            imageUrl={picture}
            color={
              subType === 'training'
                ? 'green'
                : subType === 'learningpath'
                ? 'dark_blue'
                : subType === 'milestone'
                ? 'blue'
                : subType === 'evaluation'
                ? 'purple'
                : subType === 'course'
                ? 'yellow'
                : subType === 'assessment'
                ? 'red'
                : subType === 'leervacature'
                ? 'yellow'
                : 'yellow'
            }
            size="small_md"
            className={
              subType === 'course'
                ? 'fill-primary-06 text-primary-01'
                : subType === 'assessment'
                ? 'fill-tint-red-dark text-tint-red-light'
                : subType === 'leervacature'
                ? 'fill-error-04 text-error-04'
                : ''
            }
          />
        </div>

        <div className="flex flex-col gap-1">
          <p className="text-xs text-grey-06">
            {subType === 'training'
              ? t('pages.learning_path.training')
              : subType === 'learningpath'
              ? t('pages.learning_path.theme')
              : subType === 'milestone'
              ? t('pages.learning_path.milestone')
              : subType === 'evaluation'
              ? t('pages.learning_path.evaluation')
              : subType === 'course'
              ? t('links.course')
              : subType === 'assessment'
              ? t('links.assessment')
              : subType === 'leervacature'
              ? t('links.vacancy')
              : t('links.course')}
          </p>
          <p className="text-sm font-medium">{name}</p>
        </div>
      </div>
      {type === 'learningpath' && (
        <div className="flex flex-col">
          {lpTreeConrent?.map((el: any) => {
            return (
              <SpecifyItemContent
                key={el.id}
                item={el}
                authUser={authUser}
                updateSpecifyItem={updateSpecifyItem}
                nodesTeachers={nodesTeachers}
                setContentTeachers={setContentTeachers}
                create={create}
                specifyItem={specifyItem}
                contentTeachers={contentTeachers}
              />
            );
          })}
        </div>
      )}
      {type !== 'learningpath' && (
        <>
          <div className="">
            {contentTeacher.map((item: any) => (
              <div
                key={item.userId !== null ? item.key : item.teamId}
                className="mt-4 flex items-center justify-between"
              >
                <div className="flex items-center">
                  <PersonAvatar
                    inline
                    id={item.id}
                    name={item.username || ''}
                    imageSrc={item.picture || undefined}
                  ></PersonAvatar>
                  <div className="flex flex-col pl-4">
                    <span className="text-sm font-medium text-[#231f20]">
                      {item.label}
                    </span>
                  </div>
                </div>

                <div data-testid="daleteItem" className="my-auto mx-6">
                  <IconButton
                    Icon={CrossFilled}
                    variant="ghost"
                    onClick={() => handleRemoveTeacher(item.key)}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="">
            <div
              id="invite_teacher"
              className="ali relative z-[11] flex w-full"
            >
              <span className="w-full">
                <UserInvite
                  fetch={useGet.bind(null, '/memberdetails', {
                    organisationId: authUser?.activeOrganisationId,
                    minrole: 70,
                  })}
                  inviteButtonText={t('pages.manage_add_users.add_button')}
                  value={teachers}
                  onChange={setTeachers}
                  handleAddUserToList={handleAddTeacherToList}
                  addValue={(email: string) => {
                    const newValue = [
                      ...value,
                      {
                        email: email,
                        label: email,
                        key: email,
                        id: email,
                        userId: email,
                        newUser: true,
                      },
                    ];
                    setTeachers(newValue);
                    return newValue;
                  }}
                />
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const EvaluatorsStep = ({
  state,
  setLoadStep,
  learningpath,
  treeConrent,
  create,
  loadedStep,
}: any) => {
  const {
    specifyItem,
    setSpecifyItem,
    newTeachers,
    setNewTeachers,
    contentTeachers,
    setContentTeachers,
    setEvaluatorsStep,
  } = state;

  const [updateSpecifyItem, setUpdateSpecifyItem] = useState(false);

  const [nodesTeachers, setNodesTeachers] = useState([]);
  const [showContent, setShowContent] = useState(newUid(10));

  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user as Userwithorganisationwithidps;

  const [teachers, setTeachers] = useState<any>([]);
  const [value, setValue] = useState<any>([]);

  useEffect(() => {
    setTimeout(() => {
      setLoadStep(false);
      setEvaluatorsStep(true);
      loadedStep();
    }, 100);
  }, []);

  useEffect(() => {
    if (specifyItem) {
      setNodesTeachers(newTeachers);
      setShowContent(newUid(10));
    }
  }, [specifyItem]);

  const handleAddTeacherToList = (newValue: any) => {
    const newNewTeachers = newTeachers.slice();
    for (const teacher of newValue) {
      const found = newTeachers.find(
        (oldTeacher: any) => teacher.key === oldTeacher.key
      );
      if (!found) {
        teacher.lprole = 20;
        newNewTeachers.push(teacher);
      }
    }
    setNewTeachers(newNewTeachers);
    setNodesTeachers(newNewTeachers);
    setTeachers([]);
  };

  const handleRemoveTeacher = (id: any) => {
    const removeTeacher = newTeachers.filter((item: any) => item.key !== id);
    setNewTeachers(removeTeacher);
    setNodesTeachers(removeTeacher);
  };

  return (
    <div>
      <article className="prose flex flex-col ">
        <h3 className="text-base">
          {t('pages.course_content_flow.evaluators')}
        </h3>
        <p className="pt-3 text-sm text-grey-07">
          {t('pages.follow_learningpath.step_evaluators.subtitle')}
        </p>
      </article>

      <div className="mt-4 flex flex-row items-center gap-4 rounded-lg border border-grey-02 p-4">
        <div>
          <Switch
            checked={specifyItem}
            onChange={(isCheck) => {
              !isCheck && setContentTeachers([]);
              setUpdateSpecifyItem(true);
              setSpecifyItem(isCheck);
            }}
          />
        </div>
        <div>
          <h4 className="pb-2 font-normal">
            {t('pages.follow_learningpath.step_evaluators.switch')}
          </h4>
          <p className="text-xs text-grey-05">
            {t('pages.follow_learningpath.step_evaluators.switch_description')}
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-col rounded-lg border p-4 ">
        <div className="flex items-center">
          <div className="mr-3">
            <IconTile
              Icon={DuoToneTrajectoryFilled}
              color={'yellow'}
              size="small_sm"
              className={'fill-primary-05'}
            />
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">
              {t('pages.follow_learningpath.step_evaluators.main_evaluators')}
            </p>
            <p className="text-xs text-grey-06">
              {t(
                'pages.follow_learningpath.step_evaluators.main_evaluators_description'
              )}
            </p>
          </div>
        </div>
        <div className="">
          {newTeachers.map((item: any) => (
            <div
              key={item.userId !== null ? item.key : item.teamId}
              className="mt-4 flex items-center justify-between"
            >
              <div className="flex items-center">
                <PersonAvatar
                  inline
                  id={item.id}
                  name={item.username || ''}
                  imageSrc={item.picture || undefined}
                ></PersonAvatar>
                <div className="flex flex-col pl-4">
                  <span className="text-sm font-medium text-[#231f20]">
                    {item.label}
                  </span>
                </div>
              </div>

              <div data-testid="daleteItem" className="my-auto mx-6">
                <IconButton
                  Icon={CrossFilled}
                  variant="ghost"
                  onClick={() => handleRemoveTeacher(item.key)}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="">
          <div id="invite_teacher" className="ali relative z-[11] flex w-full">
            <span className="w-full">
              <UserInvite
                fetch={useGet.bind(null, '/memberdetails', {
                  organisationId: authUser?.activeOrganisationId,
                  minrole: 70,
                })}
                inviteButtonText={t('pages.manage_add_users.add_button')}
                value={teachers}
                onChange={setTeachers}
                handleAddUserToList={handleAddTeacherToList}
                addValue={(email: string) => {
                  const newValue = [
                    ...value,
                    {
                      email: email,
                      label: email,
                      key: email,
                      id: email,
                      userId: email,
                      newUser: true,
                    },
                  ];
                  setTeachers(newValue);
                  return newValue;
                }}
              />
            </span>
          </div>
        </div>
      </div>

      {specifyItem && (
        <div className="flex flex-col" key={showContent}>
          {treeConrent?.map((el: any) => {
            return (
              <SpecifyItemContent
                key={el.id}
                item={el}
                authUser={authUser}
                updateSpecifyItem={updateSpecifyItem}
                nodesTeachers={nodesTeachers}
                setContentTeachers={setContentTeachers}
                create={create}
                specifyItem={specifyItem}
                contentTeachers={contentTeachers}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EvaluatorsStep;
